import { useState } from "react";
import { Film, CheckCircle2, ArrowRight, ExternalLink, AlertTriangle } from "lucide-react";
import ProductionPlanModal from "./ProductionPlanModal";
import { getCanonicalPlanDisplay } from "@/domain/productionPlan";

export const ProductionPlanCard = ({
  plan,
  project,
  isActive = false,
  onActivatePlan,
  onRequestChanges,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!plan) return null;

  const { displayTitle, canonicalVersion } = getCanonicalPlanDisplay(plan);
  const summarySnippet =
    plan.summary ||
    (plan.content ? plan.content.slice(0, 160).replace(/\n+/g, " ") + "..." : "Plan de producción disponible");

  return (
    <>
      <div
        data-testid="production-plan-card"
        className={`my-3 p-4 rounded-xl border transition-all ${
          isActive
            ? "bg-slate-900/90 border-emerald-500/40 shadow-sm shadow-emerald-950/20"
            : "bg-slate-900/80 border-indigo-500/30 hover:border-indigo-500/50"
        }`}
      >
        <div className="flex items-start justify-between gap-3 mb-2.5">
          <div className="flex items-center gap-2.5">
            <div
              className={`p-2 rounded-lg border ${
                isActive
                  ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                  : "bg-indigo-500/10 border-indigo-500/20 text-indigo-400"
              }`}
            >
              <Film className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-slate-100">{displayTitle}</h4>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                  {canonicalVersion}
                </span>
                {isActive && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    <CheckCircle2 className="w-3 h-3" />
                    Activo
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Hoja de ruta cinematográfica y consultiva de VEYRA
              </p>
            </div>
          </div>
        </div>

        {plan.hasDurationDiscrepancy && (
          <div
            data-testid="plan-duration-discrepancy-card"
            className="flex items-center gap-2 p-2 mb-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px]"
          >
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 text-amber-400" />
            <span>
              Discrepancia de duraciones con Storyboard ({plan.durationDiscrepancies.map((d) => `Toma ${d.order}: ${d.planDuration}s vs ${d.storyboardDuration}s`).join(", ")}).
            </span>
          </div>
        )}

        {plan.hasContractError && (
          <div
            data-testid="plan-contract-error-card"
            className="flex items-center gap-2 p-2 mb-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[11px]"
          >
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 text-rose-400" />
            <span>
              Estructura no conforme con contrato VEYRA ({plan.contractErrors.join("; ")}). Se conserva para lectura.
            </span>
          </div>
        )}

        <p className="text-xs text-slate-300 line-clamp-3 leading-relaxed mb-3.5 bg-slate-950/40 p-2.5 rounded-lg border border-slate-800">
          {summarySnippet}
        </p>

        <div className="flex items-center justify-between pt-1 border-t border-slate-800/80">
          <span className="text-[11px] text-slate-400">
            {isActive ? "Planificación activa en el proyecto" : "Borrador de planificación preparado"}
          </span>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors shadow-sm"
          >
            Revisar planificación
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </div>

      <ProductionPlanModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        plan={plan}
        project={project}
        isActive={isActive}
        onActivatePlan={onActivatePlan}
        onRequestChanges={onRequestChanges}
      />
    </>
  );
};

export default ProductionPlanCard;
